import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ui-empty"
}
const _hoisted_2 = {
  key: 0,
  class: "property-summary ui-panel mb-5"
}
const _hoisted_3 = {
  key: 0,
  class: "flex flex-wrap items-center gap-2"
}
const _hoisted_4 = { class: "font-medium" }
const _hoisted_5 = {
  key: 2,
  class: "property-managers"
}
const _hoisted_6 = {
  key: 0,
  class: "manager-badge"
}
const _hoisted_7 = { class: "staff-avatar" }
const _hoisted_8 = { class: "manager-name" }
const _hoisted_9 = {
  key: 1,
  class: "manager-badge"
}
const _hoisted_10 = { class: "staff-avatar" }
const _hoisted_11 = { class: "manager-name" }
const _hoisted_12 = { class: "property-detail-grid" }
const _hoisted_13 = { class: "min-w-0" }
const _hoisted_14 = {
  key: 0,
  class: "section-heading products-heading"
}
const _hoisted_15 = {
  key: 1,
  class: "property-products"
}
const _hoisted_16 = {
  key: 2,
  class: "ui-empty"
}
const _hoisted_17 = { class: "property-aside" }

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { MapPin, ChartNoAxesCombined } from '@lucide/vue';

import { computed, ref, onMounted, watch } from 'vue';
import axios from 'axios';
import PropertyProduct from '@/components/PropertyProduct.vue';
import SlotList from '@/components/SlotList.vue';
import PropertyContacts from '@/components/PropertyContacts.vue';

interface Props {
  propertyId: number;
  highlightedContactId?: number | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PropertyDetailContent',
  props: {
    propertyId: {},
    highlightedContactId: { default: null }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const loading = ref(true);
const property = ref<Property | null>(null);
const propertyDetailProducts = ref<PropertyDetailProduct[]>([]);
const canViewSurveys = ref(false);
const hasPropertyInfo = computed(
  () => !!(property.value?.city || property.value?.manager || property.value?.sales_manager)
);

function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

function getInitials(firstName: string, lastName: string): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last;
}

async function reload() {
  try {
    loading.value = true;
    property.value = null;
    propertyDetailProducts.value = [];

    const response = await axios.get('/api/properties/' + props.propertyId + '/');
    const data: Property = response.data;

    const versions = (data.versions ?? []).filter(isDefined);
    const supporters = (data.supporters ?? []).filter(isDefined);
    const slots = (data.slots ?? []).filter(isDefined);
    const queue = (data.queue ?? []).filter(isDefined);
    const contacts = (data.contacts ?? []).filter(isDefined);
    const modules = (data.modules ?? []).filter(isDefined);

    const propertyDetailsMap = new Map<string, PropertyDetailProduct>();

    versions.forEach((version) => {
      const supporterInfo = supporters.find((s) => s.product === version.product);
      let product = propertyDetailsMap.get(version.product);
      if (!product) {
        product = {
          product: version.product,
          productId: version.product_id,
          versions: [],
          info: supporterInfo,
          search: '',
          createdDate: version.created_at,
          propertyId: data.id,
          modules: [],
        };
        propertyDetailsMap.set(version.product, product);
      }
      product.versions.push(version);
    });

    const productWithVersion: PropertyDetailProduct[] = Array.from(
      propertyDetailsMap.values()
    ).sort((x, y) => (x.product > y.product ? 1 : -1));

    const productWithVersionIds: number[] = productWithVersion
      .map((item) => item.versions[0]?.product_id ?? -1)
      .filter((id) => id !== -1);

    const supportedProduct: PropertyDetailProduct[] = supporters
      .filter((item) => !productWithVersionIds.includes(item.product_id))
      .map((item) => ({
        propertyId: data.id,
        info: item,
        product: item.product,
        productId: item.product_id,
        createdDate: null,
        search: '',
        versions: [],
        modules: [],
      }));

    modules.forEach((module) => {
      const product = propertyDetailsMap.get(module.product_name);
      if (product) {
        product.modules.push(module);
      } else {
        const supportedProd = supportedProduct.find((p) => p.product === module.product_name);
        if (supportedProd) {
          supportedProd.modules.push(module);
        }
      }
    });

    propertyDetailProducts.value = [...productWithVersion, ...supportedProduct];
    property.value = {
      ...data,
      versions,
      supporters,
      slots,
      queue,
      contacts,
      modules,
    };
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.propertyId,
  async () => {
    await reload();
  }
);

onMounted(async () => {
  axios
    .get('/api/surveys/permissions/')
    .then((response) => {
      canViewSurveys.value = response.data.can_view === true;
    })
    .catch(() => {
      canViewSurveys.value = false;
    });
  await reload();
});

__expose({
  reload,
  property,
  loading,
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("section", null, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_unref(Spinner)),
          _cache[2] || (_cache[2] = _createElementVNode("span", null, "Загрузка объекта…", -1))
        ]))
      : _createCommentVNode("", true),
    (property.value && !loading.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (hasPropertyInfo.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                (property.value.city)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _createVNode(_unref(MapPin), {
                        size: 17,
                        class: "text-primary"
                      }),
                      _createElementVNode("span", _hoisted_4, _toDisplayString(property.value.city.name), 1),
                      (property.value.city.utc_offset !== null && property.value.city.utc_offset !== undefined)
                        ? (_openBlock(), _createBlock(_unref(Badge), {
                            key: 0,
                            variant: "outline"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(" UTC" + _toDisplayString(property.value.city.utc_offset >= 0 ? '+' : '') + _toDisplayString(property.value.city.utc_offset), 1)
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                (canViewSurveys.value)
                  ? (_openBlock(), _createBlock(_unref(Button), {
                      key: 1,
                      "as-child": "",
                      variant: "outline",
                      size: "sm",
                      "aria-label": "NPS-оценки"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_router_link, {
                          to: { name: 'surveys', query: { property_id: property.value.id } }
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(ChartNoAxesCombined), { size: 15 }),
                            _cache[3] || (_cache[3] = _createTextVNode(" NPS-оценки ", -1))
                          ]),
                          _: 1
                        }, 8, ["to"])
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (property.value.manager || property.value.sales_manager)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      (property.value.manager)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                            _createElementVNode("div", _hoisted_7, _toDisplayString(getInitials(property.value.manager.first_name, property.value.manager.last_name)), 1),
                            _createElementVNode("div", null, [
                              _cache[4] || (_cache[4] = _createElementVNode("div", { class: "manager-role" }, "Менеджер", -1)),
                              _createElementVNode("div", _hoisted_8, _toDisplayString(property.value.manager.first_name) + " " + _toDisplayString(property.value.manager.last_name), 1)
                            ])
                          ]))
                        : _createCommentVNode("", true),
                      (property.value.sales_manager)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            _createElementVNode("div", _hoisted_10, _toDisplayString(getInitials(property.value.sales_manager.first_name, property.value.sales_manager.last_name)), 1),
                            _createElementVNode("div", null, [
                              _cache[5] || (_cache[5] = _createElementVNode("div", { class: "manager-role" }, "Продажи", -1)),
                              _createElementVNode("div", _hoisted_11, _toDisplayString(property.value.sales_manager.first_name) + " " + _toDisplayString(property.value.sales_manager.last_name), 1)
                            ])
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("div", _hoisted_13, [
              (propertyDetailProducts.value.length || (!hasPropertyInfo.value && canViewSurveys.value))
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                    _cache[7] || (_cache[7] = _createElementVNode("h2", null, "Продукты", -1)),
                    (propertyDetailProducts.value.length)
                      ? (_openBlock(), _createBlock(_unref(Badge), {
                          key: 0,
                          variant: "outline"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(propertyDetailProducts.value.length), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (!hasPropertyInfo.value && canViewSurveys.value)
                      ? (_openBlock(), _createBlock(_unref(Button), {
                          key: 1,
                          "as-child": "",
                          variant: "outline",
                          size: "sm",
                          class: "ml-auto",
                          "aria-label": "NPS-оценки"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_router_link, {
                              to: { name: 'surveys', query: { property_id: property.value.id } }
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(ChartNoAxesCombined), { size: 15 }),
                                _cache[6] || (_cache[6] = _createTextVNode(" NPS-оценки ", -1))
                              ]),
                              _: 1
                            }, 8, ["to"])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              (propertyDetailProducts.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(propertyDetailProducts.value, (propertyDetailProduct) => {
                      return (_openBlock(), _createBlock(PropertyProduct, {
                        key: propertyDetailProduct.product,
                        "property-detail-product": propertyDetailProduct,
                        onRefresh: _cache[0] || (_cache[0] = ($event: any) => (reload()))
                      }, null, 8, ["property-detail-product"]))
                    }), 128))
                  ]))
                : (_openBlock(), _createElementBlock("p", _hoisted_16, "Нет установленных продуктов для данного объекта"))
            ]),
            _createElementVNode("aside", _hoisted_17, [
              (property.value.slots && property.value.slots.length)
                ? (_openBlock(), _createBlock(SlotList, {
                    key: 0,
                    slots: property.value.slots,
                    queue: property.value.queue || [],
                    onSlotChanged: _cache[1] || (_cache[1] = ($event: any) => (reload()))
                  }, null, 8, ["slots", "queue"]))
                : _createCommentVNode("", true),
              (property.value.contacts && property.value.contacts.length)
                ? (_openBlock(), _createBlock(PropertyContacts, {
                    key: 1,
                    contacts: property.value.contacts,
                    "highlighted-contact-id": __props.highlightedContactId
                  }, null, 8, ["contacts", "highlighted-contact-id"]))
                : _createCommentVNode("", true)
            ])
          ])
        ], 64))
      : _createCommentVNode("", true)
  ]))
}
}

})